import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Chunk } from '../../chunk.dto';

declare var katex: any;

@Component({
  selector: 'app-html-chunk-displayer',
  standalone: true,
  template: `
    @if (chunk(); as chunk) {
      @if (['<p></p><p></p>', '<p></p>', ''].includes(editionToDisplay()?.content)) {
        <!-- To display the empty chunk -->
        <p>&nbsp;</p>
      } @else {
        <div [innerHTML]="editionToDisplay()?.sanitizedHTML || editionToDisplay()?.content"></div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtmlChunkDisplayerComponent implements AfterViewChecked {
  chunk = input.required<Chunk>();

  private translate = inject(TranslateService);
  private elementRef = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);

  languageChanged = toSignal(this.translate.onLangChange);
  private lastRawHTML = '';
  private lastSanitizedHTML: any = null;

  editionToDisplay = computed(() => {
    this.languageChanged();
    const chunk = this.chunk();
    if (!chunk) {
      return null;
    }
    const currentLang = this.translate.currentLang;

    const editionInCurrentLang = chunk.editions.find((ed) => ed.language === currentLang);
    const edition = editionInCurrentLang || chunk.mainEdition;
    if (!edition) {
      return null;
    }

    let rawHTML = edition.content || '';
    if (rawHTML.includes('`')) {
      rawHTML = rawHTML.replace(/`([^`<>\n]+)`/g, '<code>$1</code>');
    }

    if (rawHTML !== this.lastRawHTML) {
      this.lastRawHTML = rawHTML;
      this.lastSanitizedHTML = this.sanitizer.bypassSecurityTrustHtml(rawHTML);
    }

    return {
      ...edition,
      sanitizedHTML: this.lastSanitizedHTML,
    };
  });

  ngAfterViewChecked() {
    this.renderFormulas();
  }

  private renderFormulas() {
    if (typeof katex !== 'undefined') {
      const elements = this.elementRef.nativeElement.querySelectorAll('.ql-formula');
      elements.forEach((el: HTMLElement) => {
        const formula = el.getAttribute('data-value');
        if (formula && !el.hasAttribute('data-rendered')) {
          try {
            katex.render(formula, el, {
              throwOnError: false,
            });
            el.setAttribute('data-rendered', 'true');
          } catch (e) {
            console.error('KaTeX rendering error:', e);
          }
        }
      });
    }
  }
}
